<template>
    <Teleport :disabled="menuStore.isDesktop"
              to="body">
        <SlideDownTransition :disabled="menuStore.isDesktop">
            <div v-if="loadMenu"
                 :id="NAVIGATION_ID"
                 class="nav"
                 :class="{
                     'mobile-nav': showMobileNav,
                     'nav--index-menu': indexMenu,
                 }">
                <div v-for="(item, index) in filteredMenu"
                     :key="index">
                    <RootMenuItem :menu="item" />
                </div>
                <MobileMenuFooter v-if="showMobileNav" />
            </div>
        </SlideDownTransition>
    </Teleport>
    <SubscribeModal v-if="subscribeModalStore.modals.showSubscribeModal"
                    :title="SUBSCRIBE_MODAL_TITLE"
                    :subtitle="SUBSCRIBE_MODAL_SUBTITLE"
                    :buttonTitle="SUBSCRIBE_MODAL_BUTTON_TITLE" />
    <SubscribeSuccessModal v-if="subscribeModalStore.modals.showSubscribeSuccessModal" />
</template>

<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue';
    import { useMenuStore } from '@/js/store/menu/MenuStore';
    import RootMenuItem from './menus/RootMenuItem.vue';
    import MobileMenuFooter from './mobile-menu-footer/MobileMenuFooter.vue';
    import SlideDownTransition from '../../transitions/SlideDownTransition.vue';
    import { NAVIGATION_ID } from './HtmlIDs';
    import SubscribeModal from '../../subscribe/modals/SubscribeModal.vue';
    import SubscribeSuccessModal from '../../subscribe/modals/SubscribeSuccessModal.vue';
    import { useSubscribeModalStore } from '@/js/store/subscribe/SubscribeModalStore';
    import { HideOptionEnum } from './enums/menuEnums';
    import { TMenu } from './types/menuTypes';
    import { getTrans } from '@/js/helpers/getTrans';

    const props = defineProps<{
        hideMenuOnDesktop?: boolean,
        indexMenu?: boolean,
    }>();

    const menuStore = useMenuStore();
    const subscribeModalStore = useSubscribeModalStore();

    const SUBSCRIBE_MODAL_TITLE = getTrans('subscribe.newsletterTitle', 'Get Daily Crypto Insights That Finally Click');
    const SUBSCRIBE_MODAL_SUBTITLE = getTrans('subscribe.newsletterSubtitle', 'Join 120,000+ Daily Squeeze subscribers and get our 5 min newsletter with the juicy insights, and tips to spice up your investment game daily.');
    const SUBSCRIBE_MODAL_BUTTON_TITLE = getTrans('subscribe.newsletterButtonText', 'Squeeze Me In');

    const loadMenu = computed(() => {
        if (props.hideMenuOnDesktop && menuStore.isDesktop) {
            return false;
        }

        return menuStore.isMobileNavOpen || menuStore.isDesktop;
    });

    const showMobileNav = computed(() => menuStore.isMobileNavOpen && !menuStore.isDesktop);

    const filteredMenu = computed(() => {
        if (!menuStore.menuData) {
            return [];
        }

        const menuData: TMenu[] = Array.isArray(menuStore.menuData) ? menuStore.menuData : [menuStore.menuData];
        const hideCondition = menuStore.isDesktop ? HideOptionEnum.DESKTOP : HideOptionEnum.MOBILE;

        return menuData.filter((childMenu) => childMenu?.hide !== hideCondition);
    });

    const hidePlaceholder = () => {
        const placeholder = document.getElementById('nav-placeholder');

        if (placeholder) {
            placeholder.style.display = 'none';
        }
    };

    watch(showMobileNav, (newValue) => {
        const isAppPopupClosed = sessionStorage.getItem('appPopupClosed') === 'true';
        const popupElement = document.getElementById('app-popup-wrapper');
        const headerNavigationTopMenuPromotionalBar = document.getElementById('header-navigation-top-menu-promotional-bar');

        if (popupElement && !isAppPopupClosed) {
            popupElement.style.display = newValue ? 'none' : '';
        }

        if (headerNavigationTopMenuPromotionalBar) {
            headerNavigationTopMenuPromotionalBar.style.display = newValue ? 'none' : '';
        }
    });

    onMounted(() => {
        hidePlaceholder();
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/z-index" as ZIndex;
@use "@/sass/partials/variables" as legacyVariables;

:deep(a) {
    color: unset;
}

.nav {
    display: flex;
    gap: 16px;

    &.mobile-nav {
        position: fixed;
        z-index: ZIndex.$mobile-navbar-z-index;
        top: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});
        bottom: 0;
        left: 0;
        display: flex;
        overflow: auto;
        width: 100%;
        flex-direction: column;
        flex-wrap: nowrap;
        background-color: legacyVariables.$white;
        gap: 0;

        @media (min-width: legacyVariables.$promotional-bar-3-lines-breakpoint) and (max-width: legacyVariables.$promotional-bar-2-lines-breakpoint) {
            top: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});
        }

        @media (min-width: legacyVariables.$promotional-bar-2-lines-breakpoint) {
            top: calc(#{legacyVariables.$top-menu-height} + #{legacyVariables.$navbar-height});
        }
    }

    &--index-menu.mobile-nav {
        top: calc(#{legacyVariables.$navbar-height});

        @media (min-width: legacyVariables.$promotional-bar-3-lines-breakpoint) and (max-width: legacyVariables.$promotional-bar-2-lines-breakpoint) {
            top: calc(#{legacyVariables.$navbar-height});
        }

        @media (min-width: legacyVariables.$promotional-bar-2-lines-breakpoint) {
            top: calc(#{legacyVariables.$navbar-height});
        }
    }
}

.dark-theme {
    .nav.mobile-nav {
        background: legacyVariables.$dark-theme-bg;
    }
}
</style>
